import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Checkbox,
  TextField,
  Box,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import FlagIcon from "@material-ui/icons/Flag";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import axios from "axios";
import { dateFormated } from "../../functionalComponents/dataFormatFunctions";
import { useApiBaseUrl } from "../../CommonMethods/CommonMethods";
import { appUrl } from "../../../../../../utils/config";
import BatchAllocationSuccessPopup from "./BatchAllocationSuccessPopup";

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  dialogHeader: {
    backgroundColor: "#F5F9FF",
    padding: "24px 32px 20px",
    textAlign: "center",
    position: "relative",
  },
  title: {
    fontWeight: 700,
    fontSize: 22,
  },
  subtitle: {
    fontSize: 13,
    color: "#555",
    marginTop: 4,
  },
  cancelBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    color: "#000",
    gap: 4,
    border: "none",
    background: "transparent",
    "& svg": { color: "red", fontSize: 18 },
  },
  tableContainer: {
    border: "1px solid #ddd",
    borderRadius: 4,
  },
  headerCell: {
    backgroundColor: "#f5f5f5",
    fontWeight: 700,
    fontSize: 12,
    padding: "8px 10px",
    whiteSpace: "nowrap",
    borderRight: "1px solid #e0e0e0",
    "&:last-child": { borderRight: "none" },
  },
  bodyCell: {
    fontSize: 12,
    padding: "6px 10px",
    borderRight: "1px solid #f0f0f0",
    "&:last-child": { borderRight: "none" },
  },
  bodyCellError: {
    fontSize: 12,
    padding: "6px 10px",
    borderRight: "1px solid #f0f0f0",
    color: "#d32f2f",
    fontWeight: 600,
    "&:last-child": { borderRight: "none" },
  },
  qtyInput: {
    "& .MuiOutlinedInput-root": {
      fontSize: 12,
      height: 30,
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      textAlign: "center",
    },
    width: 70,
  },
  qtyInputError: {
    "& .MuiOutlinedInput-root": {
      fontSize: 12,
      height: 30,
      "& fieldset": { borderColor: "#d32f2f" },
      "&:hover fieldset": { borderColor: "#d32f2f" },
      "&.Mui-focused fieldset": { borderColor: "#d32f2f" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      textAlign: "center",
      color: "#d32f2f",
    },
    width: 70,
  },
  checkbox: {
    padding: 4,
    color: "#ec6535",
    "&.Mui-checked": { color: "#ec6535" },
  },
  saveBtn: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 50,
    textTransform: "none",
    padding: "6px 28px",
    fontWeight: 600,
    "&:hover": { backgroundColor: "#d4562a" },
    "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" },
  },
  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    backgroundColor: "#fff3f3",
    border: "1px solid #f5c6cb",
    borderRadius: 4,
    padding: "6px 12px",
    marginBottom: 6,
    "& svg": { color: "#d32f2f", fontSize: 16, marginTop: 2 },
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 12,
  },
  legendBox: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
    fontSize: 12,
  },
  nearExpiryFlag: {
    color: "yellow",
    fontSize: 16,
    stroke: "black",
    strokeWidth: 1.5,
  },
  expiredFlag: {
    color: "red",
    fontSize: 16,
    stroke: "black",
    strokeWidth: 1.5,
  },
  tableBodyRow: {
    "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
    "&:hover": { backgroundColor: "#f0f4ff" },
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Build a unique key per (PO + material) for over-allocation grouping
const poMaterialKey = (row) => `${row.scheduleAgreementNumber}__${row.materialNumber}`;

// ─── Main Component ───────────────────────────────────────────────────────────
const EditAllocationTable = ({ open, onClose, apiResponse, uploadedFileDetails, onSaveSuccess }) => {
  const classes = useStyles();
  const API_BASE_URL = useApiBaseUrl(appUrl);
  //const responseData = (apiResponse?.validPOsList?.length > 0) ? apiResponse : mockAllocatePOsResponse;

  const responseData = apiResponse;

  // ── Local row state (qty + deplete checkbox) ──
  const [rows, setRows] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  // ── Error state (mirrors EditPlannedAllocatedQuantityDialog pattern) ──
  // inputErrors: per-row errors keyed by row._id (net available check)
  // groupErrors: per PO+Material group errors keyed by poMaterialKey (BOM/over/under allocation)
  const [inputErrors, setInputErrors] = useState({});
  const [groupErrors, setGroupErrors] = useState({});
  // groupWarnings: per PO+Material warning-band messages (BCM-613) — informational
  // only, never disables Save (hasErrors is built from groupErrors/inputErrors only)
  const [groupWarnings, setGroupWarnings] = useState({});

  // ── Init rows from API response ──
  useEffect(() => {
    if (!responseData?.validPOsList) return;

    // Base rows: auto-check deplete when Biologics + Bulk Drug Product (Bulk DP)
    const baseRows = responseData.validPOsList.map((item, idx) => ({
      ...item,
      _id: idx,
      localQty: item.isDepleted
        ? String(item.netAvailableQuantity)
        : String(item.plannedAllocatedQty || ""),
      localDepleted: Boolean(item.isDepleted),
      localDeleted: false,
    }));

    // If same batchNumber appears in 2+ different POs → force uncheck deplete for ALL those rows
    const batchPOMap = {};
    baseRows.forEach((row) => {
      if (!batchPOMap[row.batchNumber]) batchPOMap[row.batchNumber] = new Set();
      batchPOMap[row.batchNumber].add(row.scheduleAgreementNumber);
    });
    const multiPOBatches = new Set(
      Object.entries(batchPOMap)
        .filter(([, pos]) => pos.size > 1)
        .map(([batch]) => batch)
    );

    const finalRows = baseRows.map((row) => {
      if (multiPOBatches.has(row.batchNumber)) {
        return {
          ...row,
          localDepleted: false,
          localQty: String(row.plannedAllocatedQty || ""),
        };
      }
      return row;
    });

    setRows(finalRows);
  }, [responseData]);

  // ── Validate quantities in real time ──
  // Hierarchy: PO Number → Material → Batches (same pattern as EditPlannedAllocatedQuantityDialog)
  // All BOM/over/under errors go to newGroupErrors (highlights BoM Qty cell red for ALL cases).
  // Net Available exceeded errors go to newInputErrors (per-row, highlights Net Avail cell red).
  const validate = useCallback((currentRows) => {
    const newInputErrors = {};
    const newGroupErrors = {};
    const newGroupWarnings = {}; // BCM-613 warning band (informational)

    // Group rows by PO + Material (skip deleted rows entirely)
    const groups = {};
    currentRows.forEach((row) => {
      if (row.localDeleted) return;
      const key = poMaterialKey(row);
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    // 1. Cross-PO batch sum: Net Available Qty check (same batchNumber across all POs)
    const batchQtySums = {};
    const batchPONumbers = {};
    currentRows.forEach((row) => {
      if (row.localDeleted) return;
      if (!row.localDepleted) {
        const qty = parseFloat(row.localQty) || 0;
        batchQtySums[row.batchNumber] = (batchQtySums[row.batchNumber] || 0) + qty;
        if (!batchPONumbers[row.batchNumber]) batchPONumbers[row.batchNumber] = new Set();
        batchPONumbers[row.batchNumber].add(row.scheduleAgreementNumber);
      }
    });
    currentRows.forEach((row) => {
      if (row.localDeleted) return;
      if (!row.localDepleted) {
        const batchTotal = batchQtySums[row.batchNumber] || 0;
        const netAvail = parseFloat(row.netAvailableQuantity);
        if (batchTotal > netAvail) {
          const poSet = batchPONumbers[row.batchNumber] || new Set();
          const isCrossPO = poSet.size > 1;
          if (isCrossPO) {
            const poList = [...poSet].join(", ");
            newInputErrors[row._id] =
              `PO Number(s): ${poList} | BMS Material Batch Number: ${row.batchNumber} — Sum of Planned Allocated Qty. (${batchTotal.toFixed(3)}) exceeds Net Available Qty. (${netAvail.toFixed(3)}).`;
          } else {
            newInputErrors[row._id] =
              `PO Number: ${row.scheduleAgreementNumber} | BMS Material Batch Number: ${row.batchNumber} — Planned Allocated Qty. (${batchTotal.toFixed(3)}) exceeds Net Available Qty. (${netAvail.toFixed(3)}).`;
          }
        }
      }
    });

    // 2. Per PO+Material group: BOM / Over / Under allocation
    // Uses the total across ALL batches in the group — same logic for 1 or N batches.
    Object.entries(groups).forEach(([key, groupRows]) => {
      const firstRow = groupRows[0];
      const bomQty = parseFloat(firstRow.bomQty) || 0;
      const allowOverAllocation = Boolean(firstRow.allowOverAllocation);
      const overAllocationLimit = parseFloat(firstRow.overAllocationLimit) || 0;
      const underAllocationLimit = parseFloat(firstRow.allowUnderAllocationLimit) || 0;
      const allowUnderAllocation = underAllocationLimit > 0;

      // Skip group if no qty has been entered at all
      const hasAnyEntry = groupRows.some((row) => {
        const qty = row.localDepleted
          ? parseFloat(row.netAvailableQuantity) || 0
          : parseFloat(row.localQty) || 0;
        return qty > 0;
      });
      if (!hasAnyEntry) return;

      const totalEnteredQty = groupRows.reduce((sum, row) => {
        const qty = row.localDepleted
          ? parseFloat(row.netAvailableQuantity) || 0
          : parseFloat(row.localQty) || 0;
        return sum + qty;
      }, 0);

      const totalQty = parseFloat(totalEnteredQty.toFixed(3));
      const reqQty = parseFloat(bomQty.toFixed(3));

      const prefix = `PO Number: ${firstRow.scheduleAgreementNumber} | BMS Material: ${firstRow.materialNumber} / ${firstRow.materialName} | Material BoM Qty: ${bomQty.toFixed(3)} | Sum of Planned Allocated Qty: ${totalEnteredQty.toFixed(3)}`;

      // Multiple lot allocation check — takes priority over all other checks
      if (!firstRow.allowMultipleLotAllocation && groupRows.length > 1) {
        newGroupErrors[key] = `PO Number: ${firstRow.scheduleAgreementNumber} | BMS Material: ${firstRow.materialNumber} / ${firstRow.materialName} — Multiple Batch allocation is not allowed for this material. Only one BMS Material Batch Number can be allocated per PO.`;
        return;
      }

      // Compute effective upper and lower bounds
      // If over-allocation allowed → upper bound = bomQty + overLimit%, else strict = bomQty
      // If under-allocation allowed → lower bound = bomQty - underLimit%, else strict = bomQty
      const maxAllowed = allowOverAllocation
        ? parseFloat((bomQty + (overAllocationLimit * bomQty) / 100).toFixed(3))
        : reqQty;
      const minAllowed = allowUnderAllocation
        ? parseFloat((bomQty - (underAllocationLimit * bomQty) / 100).toFixed(3))
        : reqQty;

      let groupErrorMsg = "";

      if (totalQty > maxAllowed) {
        if (allowOverAllocation) {
          groupErrorMsg = `${prefix} — Over-Allocation Limit Exceeded: The total Planned Allocated Qty. (${totalEnteredQty.toFixed(3)}) exceeds the maximum allowed over-allocation limit of ${maxAllowed.toFixed(3)} (Material BoM Qty: ${bomQty.toFixed(3)} + Over-allocation: ${overAllocationLimit}%).`;
        } else {
          groupErrorMsg = `${prefix} — Total Planned Allocated Qty. (${totalEnteredQty.toFixed(3)}) exceeds Material BoM Qty. (${bomQty.toFixed(3)}).`;
        }
      } else if (totalQty < minAllowed) {
        if (allowUnderAllocation) {
          groupErrorMsg = `${prefix} — Under-Allocation Limit Exceeded: The total Planned Allocated Qty. (${totalEnteredQty.toFixed(3)}) is below the minimum allowed under-allocation limit of ${minAllowed.toFixed(3)} (Material BoM Qty: ${bomQty.toFixed(3)} - Under-allocation: ${underAllocationLimit}%).`;
        } else {
          groupErrorMsg = `${prefix} — Total Planned Allocated Qty. (${totalEnteredQty.toFixed(3)}) is less than Material BoM Qty. (${bomQty.toFixed(3)}). Sum must equal Material BoM Qty.`;
        }
      }

      if (groupErrorMsg) {
        newGroupErrors[key] = groupErrorMsg;
      } else {
        // Allocation warning band (BCM-613) — only evaluated when the group has
        // no blocking error; informational, the Save button stays enabled.
        // Band: deviation from Material BoM Qty between rangeFrom% and rangeTo%.
        const warnReqUnder = firstRow.warningMessageRequiredForUnderAllocation === true;
        const underWarnFrom = firstRow.underAllocationWarningRangeFrom;
        const underWarnTo = firstRow.underAllocationWarningRangeTo;
        const warnReqOver = firstRow.warningMessageRequiredForOverAllocation === true;
        const overWarnFrom = firstRow.overAllocationWarningRangeFrom;
        const overWarnTo = firstRow.overAllocationWarningRangeTo;

        let groupWarningMsg = "";
        if (
          totalQty < reqQty &&
          allowUnderAllocation &&
          warnReqUnder &&
          underWarnFrom != null &&
          underWarnTo != null
        ) {
          const warnUpper = parseFloat((bomQty - (underWarnFrom * bomQty) / 100).toFixed(3));
          const warnLower = parseFloat((bomQty - (underWarnTo * bomQty) / 100).toFixed(3));
          if (totalQty >= warnLower && totalQty <= warnUpper) {
            groupWarningMsg = `${prefix} — Under-Allocation Warning: The total Planned Allocated Qty. (${totalEnteredQty.toFixed(3)}) is within the warning message limit of the under-allocation (${underWarnFrom}% - ${underWarnTo}% of Material BoM Qty: ${bomQty.toFixed(3)}). You can still save.`;
          }
        } else if (
          totalQty > reqQty &&
          allowOverAllocation &&
          warnReqOver &&
          overWarnFrom != null &&
          overWarnTo != null
        ) {
          const warnLower = parseFloat((bomQty + (overWarnFrom * bomQty) / 100).toFixed(3));
          const warnUpper = parseFloat((bomQty + (overWarnTo * bomQty) / 100).toFixed(3));
          if (totalQty >= warnLower && totalQty <= warnUpper) {
            groupWarningMsg = `${prefix} — Over-Allocation Warning: The total Planned Allocated Qty. (${totalEnteredQty.toFixed(3)}) is within the warning message limit of the over-allocation (${overWarnFrom}% - ${overWarnTo}% of Material BoM Qty: ${bomQty.toFixed(3)}). You can still save.`;
          }
        }

        if (groupWarningMsg) {
          newGroupWarnings[key] = groupWarningMsg;
        }
      }
    });

    setInputErrors(newInputErrors);
    setGroupErrors(newGroupErrors);
    setGroupWarnings(newGroupWarnings);
  }, []);

  useEffect(() => {
    if (rows.length > 0) validate(rows);
  }, [rows, validate]);

  // ── Qty change handler ──
  const handleQtyChange = (id, value) => {
    if (!/^\d*\.?\d*$/.test(value)) return; // numbers only
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, localQty: value } : r))
    );
  };

  // ── Delete row (markForDeletePos rows only) ──
  const handleDeleteRow = (id) => {
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, localDeleted: true } : r))
    );
  };

  // ── Deplete checkbox toggle ──
  // On check, stash the qty that was in the field (file value or a prior manual
  // edit) so unchecking can restore it instead of leaving the field blank.
  const handleDepleteChange = (id, checked) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== id) return r;
        if (checked) {
          return {
            ...r,
            localDepleted: true,
            preDepleteQty: r.localQty,
            localQty: String(r.netAvailableQuantity),
          };
        }
        return {
          ...r,
          localDepleted: false,
          localQty: r.preDepleteQty ?? String(r.plannedAllocatedQty || ""),
        };
      })
    );
  };

  // ── Cell-level error helpers ──
  const hasInputError = (row) => !!inputErrors[row._id];
  const hasGroupError = (row) => !!groupErrors[poMaterialKey(row)];

  // ── Unique error messages for bottom display ──
  const allErrors = [
    ...new Set([
      ...Object.values(inputErrors),
      ...Object.values(groupErrors),
    ]),
  ];

  const hasErrors = allErrors.length > 0;

  // ── Unique warning messages (BCM-613) — informational only, never block Save ──
  const allWarnings = [...new Set(Object.values(groupWarnings))];

  const hasEmptyQty = rows.some(
    (r) => !r.localDeleted && !r.localDepleted && (r.localQty.trim() === "" || parseFloat(r.localQty) <= 0)
  );

  // ── Save handler ──
  const handleSave = async () => {
    if (hasErrors || isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      // Exclude deleted rows from save payload
      const activeRows = rows.filter((r) => !r.localDeleted);

      // Compute per-PO sums for over/under allocation flags
      const poSumsForSave = {};
      activeRows.forEach((row) => {
        const key = poMaterialKey(row);
        const qty = row.localDepleted
          ? parseFloat(row.netAvailableQuantity) || 0
          : parseFloat(row.localQty) || 0;
        poSumsForSave[key] = (poSumsForSave[key] || 0) + qty;
      });

      const updatedList = activeRows.map((row) => {
        const plannedAllocatedQty = row.localDepleted
          ? parseFloat(row.netAvailableQuantity) || 0
          : parseFloat(row.localQty) || 0;
        const key = poMaterialKey(row);
        const totalAllocated = poSumsForSave[key] || 0;

        // Allocation warning flags (BCM-613) — true when this PO+Material group's
        // total sits inside the configured warning band (the same condition that
        // showed the amber warning). Corda records the "...within Warning Message
        // Limit" PO-history event from them. Save is only reachable with no
        // errors, so totals here are already within the allowed limits.
        const _bomQty = parseFloat(row.bomQty) || 0;
        const _reqQty = parseFloat(_bomQty.toFixed(3));
        const _totalQty = parseFloat((totalAllocated || 0).toFixed(3));
        const _uFrom = row.underAllocationWarningRangeFrom;
        const _uTo = row.underAllocationWarningRangeTo;
        const _oFrom = row.overAllocationWarningRangeFrom;
        const _oTo = row.overAllocationWarningRangeTo;
        const underAllocationDoneWithinWarningLimit =
          _reqQty > 0 && _totalQty > 0 && _totalQty < _reqQty &&
          parseFloat(row.allowUnderAllocationLimit) > 0 &&
          row.warningMessageRequiredForUnderAllocation === true &&
          _uFrom != null && _uTo != null &&
          _totalQty >= parseFloat((_bomQty - (_uTo * _bomQty) / 100).toFixed(3)) &&
          _totalQty <= parseFloat((_bomQty - (_uFrom * _bomQty) / 100).toFixed(3));
        const overAllocationDoneWithinWarningLimit =
          _reqQty > 0 && _totalQty > _reqQty &&
          Boolean(row.allowOverAllocation) &&
          row.warningMessageRequiredForOverAllocation === true &&
          _oFrom != null && _oTo != null &&
          _totalQty >= parseFloat((_bomQty + (_oFrom * _bomQty) / 100).toFixed(3)) &&
          _totalQty <= parseFloat((_bomQty + (_oTo * _bomQty) / 100).toFixed(3));

        return {
          scheduleAgreementNumber: row.scheduleAgreementNumber,
          schrockenSLID: row.schrockenSLID,
          vendorMosymphonyId: row.vendorMosymphonyId,
          fgMaterialCode: row.fgMaterialCode,
          batchNumber: row.batchNumber,
          materialNumber: row.materialNumber,
          materialName: row.materialName,
          bomQty: row.bomQty,
          plannedAllocatedQty,
          netAvailableQuantity: row.netAvailableQuantity,
          uom: row.uom,
          expiryDate: row.expiryDate,
          productTypeName: row.productTypeName,
          drugTypeName: row.drugTypeName,
          isDepleted: row.localDepleted,
          allowOverAllocation: row.allowOverAllocation,
          allowUnderAllocation: row.allowUnderAllocation,
          allowUnderAllocationLimit: row.allowUnderAllocationLimit,
          overAllocationLimit: row.overAllocationLimit,
          allowMultipleLotAllocation: row.allowMultipleLotAllocation,
          countryOfOrigin: typeof row.countryOfOrigin === "string" ? row.countryOfOrigin.trim() : row.countryOfOrigin,
          plannedLotNumbers: typeof row.plannedLotNumbers === "string" ? row.plannedLotNumbers.trim() : row.plannedLotNumbers,
          overAllocationDone: Boolean(row.allowOverAllocation) && totalAllocated > parseFloat(row.bomQty),
          underAllocationDone: (parseFloat(row.allowUnderAllocationLimit) > 0) && totalAllocated < parseFloat(row.bomQty),
          // Allocation warning flags (BCM-613) — drive the PO-history warning event
          overAllocationDoneWithinWarningLimit,
          underAllocationDoneWithinWarningLimit,
          allocationDoneThruExcel: true,
          updatePlannedBatchFlag: row.plannedLotNumbers !== undefined,
          updateCountryOfOriginFlag: row.countryOfOrigin !== undefined,
        };
      });

      const formData = new FormData();
      // Binary file — middleware checks request.files.files
      if (uploadedFileDetails?.file) {
        formData.append('files', uploadedFileDetails.file);
      }
      // JSON payload as string field
      formData.append('validPOsList', JSON.stringify(updatedList));

      await axios.post(`${API_BASE_URL}/saveBatchAllocationExcel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccessOpen(true);
    } catch (err) {
      const msg =
        err?.response?.data?.errorDescription ||
        "Save failed. Please try again.";
      setSaveError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { display: "flex", flexDirection: "column", maxHeight: "90vh", width: "95vw", maxWidth: "95vw" } }}
    >
      {/* ── Header ── */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.title}>
          Edit Material Planned Allocated Qty. for POs
        </Typography>
        <button className={classes.cancelBtn} onClick={onClose}>
          Cancel <CloseIcon />
        </button>
      </Box>

      {/* ── Scrollable content (single scrollbar) ── */}
      <Box padding="16px 20px 0" style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="small" style={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>PO Number</TableCell>
                <TableCell className={classes.headerCell}>
                  BMS Material Code /<br />Description
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Material<br />BoM Qty.
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  UoM
                </TableCell>
                <TableCell className={classes.headerCell}>
                  BMS Material<br />Batch Number
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Net Available<br />Qty.
                </TableCell>
                {/* Date of Manufacturing — display only, fetched from Inventory. */}
                <TableCell className={classes.headerCell} align="center">
                  Date of<br />Manufacturing
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Expiry Date
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Planned<br />Allocated Qty.
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Add Deplete<br />Instruction
                </TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.filter((r) => !r.localDeleted).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" style={{ padding: "32px 0", color: "#888", fontSize: 13 }}>
                    No data available
                  </TableCell>
                </TableRow>
              ) : rows.filter((r) => !r.localDeleted).map((row) => {
                const rowErr = hasInputError(row);   // net available exceeded
                const grpErr = hasGroupError(row);   // BOM / over / under allocation
                const isExpired = Boolean(row.expiryDate && new Date(row.expiryDate) < new Date());
                const nearExpiry = !isExpired && Boolean(row.batchNearExpiry);

                return (
                  <TableRow key={row._id} className={classes.tableBodyRow}>
                    {/* PO Number */}
                    <TableCell className={classes.bodyCell}>
                      {row.scheduleAgreementNumber}
                    </TableCell>

                    {/* BMS Material Code / Description */}
                    <TableCell className={classes.bodyCell}>
                      {row.materialNumber} / {row.materialName}
                    </TableCell>

                    {/* Material BoM Qty — red if group (BOM/over/under) error */}
                    <TableCell
                      className={grpErr ? classes.bodyCellError : classes.bodyCell}
                      align="center"
                    >
                      {row.bomQty}
                    </TableCell>

                    {/* UoM */}
                    <TableCell className={classes.bodyCell} align="center">
                      {row.uom}
                    </TableCell>

                    {/* BMS Material Batch Number + near-expiry flag */}
                    <TableCell className={classes.bodyCell}>
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        {row.batchNumber}
                        {isExpired ? (
                          <FlagIcon className={classes.expiredFlag} />
                        ) : nearExpiry && (
                          <FlagIcon className={classes.nearExpiryFlag} />
                        )}
                      </Box>
                    </TableCell>

                    {/* Net Available Qty — red if row-level net available error */}
                    <TableCell
                      className={rowErr ? classes.bodyCellError : classes.bodyCell}
                      align="center"
                    >
                      {row.netAvailableQuantity}
                    </TableCell>

                    {/* Date of Manufacturing — display only. Backend field is
                        dateOfManufacturing (fetched from Inventory via uploadBatchAllocationExcel). */}
                    <TableCell className={classes.bodyCell} align="center">
                      {row.dateOfManufacturing ? dateFormated(row.dateOfManufacturing) : ""}
                    </TableCell>

                    {/* Expiry Date */}
                    <TableCell className={classes.bodyCell} align="center">
                      {dateFormated(row.expiryDate)}
                    </TableCell>

                    {/* Planned Allocated Qty — editable; red border on any error */}
                    <TableCell className={classes.bodyCell} align="center">
                      <TextField
                        variant="outlined"
                        value={row.localQty}
                        disabled={row.localDepleted}
                        onChange={(e) => handleQtyChange(row._id, e.target.value)}
                        className={
                          rowErr || grpErr
                            ? classes.qtyInputError
                            : classes.qtyInput
                        }
                        inputProps={{ maxLength: 10 }}
                      />
                    </TableCell>

                    {/* Add Deplete Instruction */}
                    <TableCell className={classes.bodyCell} align="center">
                      <Checkbox
                        checked={row.localDepleted}
                        onChange={(e) =>
                          handleDepleteChange(row._id, e.target.checked)
                        }
                        className={classes.checkbox}
                        size="small"
                      />
                    </TableCell>

                    {/* Action — delete icon on every row */}
                    <TableCell className={classes.bodyCell} align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(row._id)}
                        title="Remove this row from allocation"
                      >
                        <DeleteOutlineIcon style={{ color: "#d32f2f", fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Validation error messages ── */}
        {allErrors.length > 0 && (
          <Box marginTop="12px">
            {allErrors.map((msg, i) => (
              <Box key={i} className={classes.errorBox}>
                <WarningIcon />
                <Typography className={classes.errorText}>{msg}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Allocation warning messages (BCM-613) — informational only; Save stays enabled ── */}
        {allWarnings.length > 0 && (
          <Box marginTop="12px">
            {allWarnings.map((msg, i) => (
              <Box key={i} className={classes.errorBox} style={{ backgroundColor: "#FFF4E5" }}>
                <WarningIcon style={{ color: "#ED6C02" }} />
                <Typography className={classes.errorText} style={{ color: "#ED6C02" }}>{msg}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Save API error ── */}
        {saveError && (
          <Box marginTop="8px" className={classes.errorBox}>
            <WarningIcon />
            <Typography className={classes.errorText}>{saveError}</Typography>
          </Box>
        )}

        {/* ── Legend ── */}
        {(() => {
          const activeRows = rows.filter((r) => !r.localDeleted);
          const hasNearExpiry = activeRows.some((r) => Boolean(r.batchNearExpiry));
          const hasExpired = activeRows.some((r) => r.expiryDate && new Date(r.expiryDate) < new Date());
          if (!hasNearExpiry && !hasExpired) return null;
          return (
            <Box className={classes.legendBox}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Legend:
              </Typography>
              {hasNearExpiry && (
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  <FlagIcon className={classes.nearExpiryFlag} />
                  <Typography variant="caption">Material Near Expiry</Typography>
                </Box>
              )}
              {hasExpired && (
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                  <FlagIcon className={classes.expiredFlag} />
                  <Typography variant="caption">Batch Expired</Typography>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>

      {/* ── Footer: Save button ── */}
      <Box
        display="flex"
        justifyContent="center"
        padding="16px 20px 24px"
      >
        <Button
          className={classes.saveBtn}
          onClick={handleSave}
          disabled={hasErrors || hasEmptyQty || isSaving}
          endIcon={
            isSaving ? (
              <CircularProgress size={14} style={{ color: "#fff" }} />
            ) : null
          }
        >
          Save changes &gt;
        </Button>
      </Box>

      {/* ── Success popup ── */}
      <BatchAllocationSuccessPopup
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          if (onSaveSuccess) onSaveSuccess();
        }}
      />
    </Dialog>
  );
};

export default EditAllocationTable;
